// src/contexts/AuthContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { authService } from '../services/authService';
import { userService } from '../services/userService';
import { ROLES, normalizeRole } from '../utils/roles';
import { Box, Stack, Divider, Skeleton } from '@mui/material';

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

const AuthLoadingSkeleton = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      backgroundColor: '#f8f9fa',
    }}
  >
    <Skeleton variant="rectangular" width="100%" height={60} sx={{ flexShrink: 0 }} />
    <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      <Stack
        spacing={2}
        sx={{
          width: 240,
          flexShrink: 0,
          p: 2,
          borderRight: '1px solid #e0e0e0',
          backgroundColor: '#ffffff',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton variant="text" width={120} height={20} />
        </Box>
        <Divider />
        {Array.from({ length: 6 }).map((_, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Skeleton variant="rectangular" width={20} height={20} sx={{ borderRadius: 1 }} />
            <Skeleton variant="text" width={`${60 + i * 8}%`} height={18} />
          </Box>
        ))}
      </Stack>
      <Box sx={{ flex: 1, p: 3 }}>
        <Skeleton variant="text" width={220} height={36} sx={{ mb: 3 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
        <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1, mb: 1 }} />
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            width="100%"
            height={52}
            sx={{ borderRadius: 1, mb: 0.5 }}
          />
        ))}
      </Box>
    </Box>
  </Box>
);

const PUBLIC_ROUTES = ['/public-asset', '/login', '/unauthorized', '/request-access'];

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState(null);
  const isMounted = useRef(true);

  const isPublicRoute = useMemo(() => {
    const currentPath =
      typeof window !== 'undefined' ? window.location.pathname : '/';
    return PUBLIC_ROUTES.some((route) => currentPath.startsWith(route));
  }, []);

  const clearStorageData = () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('userData');
      localStorage.removeItem('companyData');
    }
  };

  const setUserProfile = useCallback((profile) => {
    if (!isMounted.current) return;
    if (profile) {
      setCurrentUser(profile);
      if (typeof window !== 'undefined') {
        localStorage.setItem('userData', JSON.stringify(profile));
      }
    } else {
      setCurrentUser(null);
      clearStorageData();
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    let unsubscribe = null;

    try {
      unsubscribe = authService.subscribeToAuthChanges(async (authUser) => {
        if (!isMounted.current) return;

        if (!authUser) {
          clearStorageData();
          setCurrentUser(null);
          setInitializing(false);
          return;
        }

        // Restore full DB profile from localStorage on page refresh (F5)
        let fullProfile = null;
        if (typeof window !== 'undefined') {
          try {
            const cached = localStorage.getItem('userData');
            if (cached) fullProfile = JSON.parse(cached);
          } catch (e) {}
        }

        // If not in localStorage, fetch from Firestore
        if (!fullProfile) {
          const uid = authUser.uid || authUser.id || authUser.userId;
          fullProfile = await userService.fetchUserById(uid);
        }

        const finalUser = fullProfile || userService.buildFallbackProfile(authUser);

        if (isMounted.current) {
          setCurrentUser(finalUser);
          if (finalUser) {
            localStorage.setItem('userData', JSON.stringify(finalUser));
          }
          setInitializing(false);
        }
      });
    } catch (err) {
      console.error('[AuthContext] Failed to subscribe to auth changes:', err);
      if (isMounted.current) {
        clearStorageData();
        setError('Authentication service unavailable. Please refresh the page.');
        setInitializing(false);
      }
    }

    return () => {
      isMounted.current = false;
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      // Just authenticate. Login.js will fetch the DB profile & call setUserProfile.
      const authProfile = await authService.login(email, password);
      return authProfile;
    } catch (err) {
      if (isMounted.current) setError(err.message || 'Login failed.');
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    setError(null);
    try {
      await authService.logout();
      clearStorageData();
      if (isMounted.current) setCurrentUser(null);
    } catch (err) {
      if (isMounted.current) setError('Failed to logout.');
      throw err;
    }
  }, []);

  const updatePassword = useCallback(async (newPassword) => {
    setError(null);
    try {
      await authService.updatePassword(newPassword);
    } catch (err) {
      if (isMounted.current) setError(err.message || 'Failed to update password.');
      throw err;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    if (!authService.auth?.currentUser) return null;
    try {
      const refreshedProfile = await userService.fetchUserById(
        authService.auth.currentUser.uid
      );
      if (isMounted.current && refreshedProfile) {
        setUserProfile(refreshedProfile);
      }
      return refreshedProfile;
    } catch (err) {
      console.error('[AuthContext] Failed to refresh user profile:', err);
      return null;
    }
  }, [setUserProfile]);

  const role = useMemo(
    () => (currentUser ? normalizeRole(currentUser.role) : null),
    [currentUser]
  );
  const isSuperAdmin = role === ROLES.SUPERADMIN;
  const isAdmin = role === ROLES.ADMIN || isSuperAdmin;
  const isLocationAdmin = role === ROLES.LOCATION_ADMIN;

  const value = useMemo(
    () => ({
      currentUser,
      user: currentUser,
      role,
      isAdmin,
      isSuperAdmin,
      isLocationAdmin,
      login,
      logout,
      updatePassword,
      refreshUser,
      setUserProfile,
      clearError,
      loading: initializing,
      error,
      isAuthenticated: Boolean(currentUser),
    }),
    [
      currentUser,
      role,
      isAdmin,
      isSuperAdmin,
      isLocationAdmin,
      initializing,
      error,
      login,
      logout,
      updatePassword,
      refreshUser,
      setUserProfile,
      clearError,
    ],
  );

  if (initializing && !isPublicRoute) {
    return <AuthLoadingSkeleton />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}